/**
 * DOCUMENTS & DRAWINGS — the first visual construction information surface.
 *
 * What this replaces was a file manager: a vertical stack of rows, each
 * carrying two selects, a number input and two icon buttons, with no way to
 * look at anything. A superintendent could see that a drawing had been
 * uploaded and could not see the drawing.
 *
 * The shape now is the one construction people already think in:
 *
 *     DOCUMENT TYPE  →  DOCUMENT  →  WHAT IT IS  →  WHAT WE KNOW ABOUT IT
 *     (rail)            (list)       (workspace)    (context)
 *
 * The centre pane is the dominant surface, because the point of the page is
 * to look at the project rather than to read a table about it.
 *
 * ON A PHONE THIS RECOMPOSES RATHER THAN SHRINKS. A 412px screen gets a
 * list, then a viewer, then details — the same information in sequence
 * instead of three columns compressed into a strip. The step is driven by
 * `?doc=<id>` in the URL, so the hardware back button walks back out of a
 * drawing the way a field user expects and a deep link opens the one it
 * names.
 *
 * WHAT THIS PAGE DOES NOT DO, and will not until a backend contract exists:
 * markup, coordinate-anchored comments, sheet pins, revision comparison,
 * overlays, clash detection, BIM/IFC, 3D/4D/5D, photo progress detection,
 * drawing interpretation, spatial coordinates. There is no disabled button
 * for any of them — a greyed control is a promise, and an absent one is the
 * truth. The asks are written down in
 * docs/OMNAS_DRAWING_WORKSPACE_1_BACKEND_ASKS.md.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useDropzone } from "react-dropzone";
import {
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  Info,
  Maximize2,
  Minimize2,
  Rows3,
  Rows4,
  ServerCog,
  Upload,
  X,
} from "lucide-react";
import { useProject } from "../hooks/useProjects";
import documentsService from "../services/documentsService";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Progress } from "../components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  SectionError,
  SectionLoading,
} from "../components/workflow/WorkflowStates";
import { useDocumentWorkspace } from "../features/documents/useDocumentWorkspace";
import {
  DOCUMENT_CATEGORIES,
  categoryBuckets,
  filterDocuments,
} from "../features/documents/documentPresentation";
import {
  DocumentList,
  DocumentRail,
  DocumentSearch,
} from "../features/documents/DocumentBrowser";
import { DocumentPreview } from "../features/documents/DocumentPreview";
import { DocumentContextPanel } from "../features/documents/DocumentContextPanel";

/**
 * WHICH COMPOSITION, not which stylesheet.
 *
 * The phone does not get the desktop's three panes with two of them hidden:
 * it gets a different arrangement of the same information. Rendering both
 * trees and hiding one with CSS would put two previews, two document lists
 * and two copies of every id in the DOM — the second of which would quietly
 * steal the list's focus ref and leave a keyboard user pressing arrows in an
 * element nobody can see.
 *
 * 1024px is Tailwind's `lg`, so this and the class names agree by
 * construction rather than by coincidence.
 */
const DESKTOP_QUERY = "(min-width: 1024px)";

const useIsDesktop = () => {
  const read = () =>
    typeof window !== "undefined" && typeof window.matchMedia === "function"
      ? window.matchMedia(DESKTOP_QUERY).matches
      : true;
  const [isDesktop, setIsDesktop] = useState(read);
  useEffect(() => {
    if (
      typeof window === "undefined" ||
      typeof window.matchMedia !== "function"
    )
      return undefined;
    const mql = window.matchMedia(DESKTOP_QUERY);
    const onChange = (event) => setIsDesktop(event.matches);
    mql.addEventListener("change", onChange);
    setIsDesktop(mql.matches);
    return () => mql.removeEventListener("change", onChange);
  }, []);
  return isDesktop;
};

/**
 * The backend refused before it read the file. Standing banner rather than a
 * toast: this is a condition of the environment that will still be true in
 * four seconds, and telling an operator "Upload failed" would send them
 * hunting for a fault in a file that is fine.
 */
const StorageNotConfiguredNotice = ({ refusal, onDismiss }) => (
  <Card
    data-testid="storage-not-configured"
    className="border-amber-500/40 bg-amber-500/5"
  >
    <CardContent className="flex items-start gap-3 p-4">
      <ServerCog
        className="mt-0.5 h-5 w-5 shrink-0 text-amber-400"
        aria-hidden="true"
      />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-foreground">
          {refusal.headline}
        </p>
        <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
          {refusal.detail}
        </p>
      </div>
      <Button
        variant="ghost"
        size="icon"
        onClick={onDismiss}
        aria-label="Dismiss storage notice"
      >
        <X className="h-4 w-4" />
      </Button>
    </CardContent>
  </Card>
);

const UploadPanel = ({
  categories,
  category,
  onCategoryChange,
  onDrop,
  uploading,
  uploadProgress,
}) => {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: true,
  });
  return (
    <div className="flex flex-col gap-3">
      <div>
        <Label htmlFor="upload-category" className="text-xs">
          Upload as
        </Label>
        <Select value={category} onValueChange={onCategoryChange}>
          <SelectTrigger
            id="upload-category"
            data-testid="upload-category"
            className="mt-1 h-9 bg-muted/50"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {categories.map((cat) => (
              <SelectItem key={cat.value} value={cat.value}>
                {cat.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div
        {...getRootProps()}
        data-testid="upload-dropzone"
        className={`cursor-pointer rounded-lg border-2 border-dashed px-4 py-5 text-center transition-colors ${
          isDragActive
            ? "border-primary bg-primary/10"
            : "border-border hover:border-primary/50"
        }`}
      >
        <input {...getInputProps()} aria-label="Upload documents" />
        <Upload
          className="mx-auto mb-2 h-6 w-6 text-muted-foreground"
          aria-hidden="true"
        />
        <p className="text-xs text-muted-foreground">
          {isDragActive
            ? "Drop files here…"
            : "Drag & drop, or tap to choose files"}
        </p>
        <p className="mt-1 text-[0.7rem] text-muted-foreground/80">
          PDF, images, Office, CAD, archives — up to 100 MB
        </p>
      </div>
      {uploading && (
        <div data-testid="upload-progress">
          <div className="mb-1 flex items-center justify-between text-xs">
            <span className="text-muted-foreground">Uploading…</span>
            <span className="font-mono">{uploadProgress}%</span>
          </div>
          <Progress value={uploadProgress} className="h-1.5" />
        </div>
      )}
    </div>
  );
};

const DocumentsPage = () => {
  const { projectId } = useParams();
  const { project, loading: projectLoading } = useProject(projectId);
  const isDesktop = useIsDesktop();

  /**
   * The selection is URL state, not component state. That is what gives the
   * mobile flow a real back button and makes a link to a drawing open the
   * drawing. `replace` is deliberate on selection changes within a session so
   * the back button leaves the viewer rather than stepping through every
   * document that was glanced at.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedParam = searchParams.get("doc");

  const selectDocument = useCallback(
    (id) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (id) next.set("doc", id);
          else next.delete("doc");
          return next;
        },
        { replace: Boolean(selectedParam) },
      );
    },
    [setSearchParams, selectedParam],
  );

  const workspace = useDocumentWorkspace(projectId, {
    selectedId: selectedParam,
    onSelect: selectDocument,
  });

  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("all");
  const [uploadCategory, setUploadCategory] = useState("general");
  const [compact, setCompact] = useState(false);
  const [fullScreen, setFullScreen] = useState(false);
  const [mobileDetailsOpen, setMobileDetailsOpen] = useState(false);
  const [reviewQueueTotal, setReviewQueueTotal] = useState(null);

  const listContainerRef = useRef(null);
  const fullScreenCloseRef = useRef(null);

  const buckets = useMemo(
    () => categoryBuckets(workspace.documents),
    [workspace.documents],
  );
  const visible = useMemo(
    () => filterDocuments(workspace.documents, { query, category }),
    [workspace.documents, query, category],
  );

  /**
   * A category that empties out (its last document deleted, or a search that
   * excludes it) would otherwise leave the rail pointing at a bucket that no
   * longer exists, showing an empty list with no way to tell why.
   */
  useEffect(() => {
    if (category !== "all" && !buckets.some((b) => b.value === category))
      setCategory("all");
  }, [buckets, category]);

  /** The tenant-wide review queue. Its own call, its own failure. */
  useEffect(() => {
    let cancelled = false;
    documentsService
      .getReviewQueue()
      .then((data) => {
        if (!cancelled)
          setReviewQueueTotal(
            typeof data?.total === "number" ? data.total : null,
          );
      })
      .catch(() => {
        if (!cancelled) setReviewQueueTotal(null);
      });
    return () => {
      cancelled = true;
    };
  }, [workspace.documents]);

  /**
   * Focus after a deletion. The hook decides WHICH row should have it; this
   * effect is the only part that needs a DOM. When the list is emptied the
   * container itself takes focus, so the user is left at the list rather than
   * at the top of the page.
   */
  const { focusRequestId, clearFocusRequest } = workspace;
  useEffect(() => {
    if (!focusRequestId) return;
    const container = listContainerRef.current;
    const target =
      focusRequestId === "__list__"
        ? container
        : container?.querySelector(
            `[data-testid="document-row-${focusRequestId}"]`,
          );
    if (target) target.focus();
    clearFocusRequest();
  }, [focusRequestId, clearFocusRequest]);

  /** Escape leaves full screen, and the close control holds focus while open. */
  useEffect(() => {
    if (!fullScreen) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape") setFullScreen(false);
    };
    document.addEventListener("keydown", onKey);
    fullScreenCloseRef.current?.focus();
    return () => document.removeEventListener("keydown", onKey);
  }, [fullScreen]);

  const onDrop = useCallback(
    async (files) => {
      for (const file of files) {
        // Sequential on purpose: the progress bar reports one upload, and a
        // parallel burst would make it report whichever finished last.
        // eslint-disable-next-line no-await-in-loop
        await workspace.uploadDocument(file, { category: uploadCategory });
      }
    },
    [workspace, uploadCategory],
  );

  const handleDelete = useCallback(
    async (doc) => {
      if (
        !window.confirm(
          `Delete "${doc.name}"? This removes the record and the stored file.`,
        )
      )
        return;
      await workspace.deleteDocument(doc);
    },
    [workspace],
  );

  if (projectLoading) {
    return <SectionLoading label="Loading project…" />;
  }

  const selected = workspace.selectedDocument;
  const previewNode = (
    <DocumentPreview
      document={selected}
      loading={workspace.detailLoading}
      onDownload={workspace.downloadDocument}
    />
  );

  /**
   * One preview element in the DOM at a time. While the full-screen viewer is
   * open the inline copy is not rendered: two live iframes of the same PDF is
   * waste, and two elements carrying the same test id is a page that cannot
   * be asserted about unambiguously.
   */
  const inlinePreview = fullScreen ? null : previewNode;

  const contextNode = (
    <DocumentContextPanel
      document={selected}
      onUpdate={workspace.updateDocument}
      onDelete={handleDelete}
      onDownload={workspace.downloadDocument}
      mutating={workspace.mutating}
    />
  );

  const listNode = workspace.loading ? (
    <SectionLoading label="Loading documents…" />
  ) : workspace.loadError ? (
    <SectionError
      error={workspace.loadError}
      onRetry={workspace.reload}
      label="Documents failed to load"
    />
  ) : workspace.contractError ? (
    /*
      The envelope could not be read. This is neither an empty project nor a
      failed request, and it is not rendered as either.
    */
    <div
      data-testid="document-contract-error"
      className="px-4 py-10 text-center"
    >
      <p className="text-sm font-medium text-foreground">
        The document list could not be read
      </p>
      <p className="mx-auto mt-1 max-w-sm text-xs text-muted-foreground">
        {workspace.contractError}
      </p>
      <Button
        variant="outline"
        size="sm"
        className="mt-3"
        onClick={workspace.reload}
      >
        Retry
      </Button>
    </div>
  ) : (
    <DocumentList
      documents={visible}
      unreadable={workspace.unreadable}
      selectedId={workspace.selectedId}
      onSelect={workspace.select}
      compact={compact}
      emptyHint={
        workspace.documents.length === 0
          ? "Nothing has been uploaded to this project yet."
          : "Adjust the search or category."
      }
    />
  );

  return (
    <div className="animate-fade-in space-y-4" data-testid="documents-page">
      {/* ------------------------------------------------------------- *
       * HEADER
       * ------------------------------------------------------------- */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <h1 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl">
            Documents &amp; Drawings
          </h1>
          <p className="mt-0.5 truncate text-sm text-muted-foreground">
            {project?.name || "Project"}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {reviewQueueTotal !== null && (
            <span
              data-testid="review-queue-count"
              className="rounded-md border border-amber-500/40 px-2.5 py-1 text-xs text-amber-300"
            >
              <span className="font-mono font-semibold">
                {reviewQueueTotal}
              </span>{" "}
              awaiting review
            </span>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCompact((v) => !v)}
            data-testid="toggle-density"
            aria-pressed={compact}
          >
            {compact ? (
              <Rows3 className="h-3.5 w-3.5" />
            ) : (
              <Rows4 className="h-3.5 w-3.5" />
            )}
            {compact ? "Comfortable" : "Compact"}
          </Button>
        </div>
      </div>

      {workspace.storageRefusal && (
        <StorageNotConfiguredNotice
          refusal={workspace.storageRefusal}
          onDismiss={workspace.dismissStorageRefusal}
        />
      )}

      {/* ------------------------------------------------------------- *
       * DESKTOP — three panes, the workspace dominant
       * ------------------------------------------------------------- */}
      {isDesktop && (
        <div className="grid grid-cols-[13rem_minmax(0,1fr)_20rem] gap-4">
          <div className="flex min-w-0 flex-col gap-4">
            <Card className="stat-card">
              <CardContent className="p-2">
                <DocumentRail
                  buckets={buckets}
                  activeCategory={category}
                  onSelectCategory={setCategory}
                />
              </CardContent>
            </Card>
            <Card className="stat-card">
              <CardContent className="p-3">
                <UploadPanel
                  categories={DOCUMENT_CATEGORIES}
                  category={uploadCategory}
                  onCategoryChange={setUploadCategory}
                  onDrop={onDrop}
                  uploading={workspace.uploading}
                  uploadProgress={workspace.uploadProgress}
                />
              </CardContent>
            </Card>
          </div>

          <div className="flex min-w-0 flex-col gap-3">
            <DocumentSearch
              value={query}
              onChange={setQuery}
              resultCount={visible.length}
            />
            <Card className="stat-card min-h-[26rem] overflow-hidden">
              <CardContent className="relative h-[26rem] p-0">
                {selected && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setFullScreen(true)}
                    data-testid="fullscreen-open"
                    className="absolute right-3 top-3 z-10"
                  >
                    <Maximize2 className="h-3.5 w-3.5" />
                    Full screen
                  </Button>
                )}
                {inlinePreview}
              </CardContent>
            </Card>
            <Card className="stat-card">
              <CardContent
                ref={listContainerRef}
                tabIndex={-1}
                className="max-h-[22rem] overflow-y-auto p-0"
              >
                {listNode}
              </CardContent>
            </Card>
          </div>

          <Card className="stat-card self-start">
            <CardContent className="max-h-[calc(100vh-10rem)] overflow-y-auto p-0">
              {contextNode}
            </CardContent>
          </Card>
        </div>
      )}

      {/* ------------------------------------------------------------- *
       * PHONE — list, then viewer, then details. Not a squeezed grid.
       * ------------------------------------------------------------- */}
      {!isDesktop && (
        <div className="flex flex-col gap-3">
          {!selected ? (
            <>
              <DocumentSearch
                value={query}
                onChange={setQuery}
                resultCount={visible.length}
              />
              {/*
              The rail becomes a horizontal strip. The scroller is INSIDE this
              element, so the document itself never scrolls sideways — the one
              thing a field user cannot recover from.
            */}
              <div className="-mx-1 overflow-x-auto px-1 pb-1">
                <div className="flex w-max gap-1.5">
                  {buckets.map((bucket) => (
                    <button
                      key={bucket.value}
                      type="button"
                      data-testid={`document-chip-${bucket.value}`}
                      aria-current={
                        bucket.value === category ? "true" : undefined
                      }
                      onClick={() => setCategory(bucket.value)}
                      className={`min-h-[2.25rem] shrink-0 rounded-full px-3 py-1.5 text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                        bucket.value === category
                          ? "bg-primary/20 font-semibold text-foreground"
                          : "bg-muted/50 text-muted-foreground"
                      }`}
                    >
                      {bucket.label}
                      <span className="ml-1.5 font-mono text-[0.65rem] opacity-70">
                        {bucket.count}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
              <Card className="stat-card">
                <CardContent
                  ref={listContainerRef}
                  tabIndex={-1}
                  className="p-0"
                >
                  {listNode}
                </CardContent>
              </Card>
              <Card className="stat-card">
                <CardContent className="p-3">
                  <UploadPanel
                    categories={DOCUMENT_CATEGORIES}
                    category={uploadCategory}
                    onCategoryChange={setUploadCategory}
                    onDrop={onDrop}
                    uploading={workspace.uploading}
                    uploadProgress={workspace.uploadProgress}
                  />
                </CardContent>
              </Card>
            </>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => workspace.select(null)}
                  data-testid="mobile-back"
                  className="min-h-[2.5rem]"
                >
                  <ArrowLeft className="h-4 w-4" />
                  All documents
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setFullScreen(true)}
                  data-testid="fullscreen-open-mobile"
                  className="min-h-[2.5rem]"
                >
                  <Maximize2 className="h-3.5 w-3.5" />
                  Full screen
                </Button>
              </div>
              <p className="break-words text-sm font-medium text-foreground">
                {selected.name}
              </p>
              <Card className="stat-card overflow-hidden">
                <CardContent className="h-[24rem] p-0">
                  {inlinePreview}
                </CardContent>
              </Card>
              {/*
              Details are a disclosure below the preview rather than a sheet
              over it: consequential metadata stays reachable without covering
              the download and back controls the user came here to reach.
            */}
              <Button
                variant="outline"
                onClick={() => setMobileDetailsOpen((v) => !v)}
                data-testid="mobile-details-toggle"
                aria-expanded={mobileDetailsOpen}
                aria-controls="mobile-details"
                className="min-h-[2.75rem] justify-between"
              >
                <span className="flex items-center gap-2">
                  <Info className="h-4 w-4" />
                  Document details
                </span>
                {mobileDetailsOpen ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
              </Button>
              {mobileDetailsOpen && (
                <Card id="mobile-details" className="stat-card">
                  <CardContent className="p-0">{contextNode}</CardContent>
                </Card>
              )}
            </>
          )}
        </div>
      )}

      {/* ------------------------------------------------------------- *
       * FULL SCREEN — the whole viewport for the drawing
       * ------------------------------------------------------------- */}
      {fullScreen && selected && (
        <div
          data-testid="fullscreen-viewer"
          role="dialog"
          aria-modal="true"
          aria-label={`${selected.name} — full screen`}
          className="fixed inset-0 z-50 flex flex-col bg-background"
        >
          <div className="flex items-center justify-between gap-3 border-b border-border px-3 py-2">
            <p className="min-w-0 flex-1 truncate text-sm font-medium">
              {selected.name}
            </p>
            <Button
              ref={fullScreenCloseRef}
              variant="ghost"
              size="sm"
              onClick={() => setFullScreen(false)}
              data-testid="fullscreen-close"
              className="min-h-[2.5rem]"
            >
              <Minimize2 className="h-4 w-4" />
              Close
            </Button>
          </div>
          <div className="min-h-0 flex-1">{previewNode}</div>
        </div>
      )}
    </div>
  );
};

export default DocumentsPage;
